import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CopyReadingButton } from "../components/CopyReadingButton";
import { api, fmtErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Heart, MoonStar, Sparkles, PenLine, Crown, History, Loader2 } from "lucide-react";
import { MarkdownText } from "../components/MarkdownText";

const CITIES = ["Roma", "Milano", "Napoli", "Torino", "Palermo", "Genova", "Bologna", "Firenze", "Bari", "Catania", "Venezia", "Verona", "Padova", "Trieste", "Brescia", "Parma", "Taranto", "Modena", "Perugia", "Livorno", "Cagliari", "Foggia", "Rimini", "Salerno", "Ferrara", "Pescara", "Bergamo", "Trento", "Bolzano", "Ancona", "Udine", "Lecce", "Pisa", "Como", "Treviso", "Siracusa", "Messina", "Reggio Calabria", "Sassari", "Potenza", "L'Aquila", "Aosta", "Campobasso"];

const empty = { name: "", birth_date: "", birth_time: "", birth_place: "" };

const PersonForm = ({ label, person, setPerson, prefix }) => (
  <div className="rounded-xl border border-[#4a2175] bg-[#0f0617] p-5" data-testid={`${prefix}-block`}>
    <p className="font-display text-amber-100 text-sm mb-4">{label}</p>
    <div className="space-y-3">
      <input
        type="text"
        value={person.name}
        onChange={(e) => setPerson({ ...person, name: e.target.value })}
        placeholder="Nome"
        maxLength={60}
        data-testid={`${prefix}-name`}
        className="input-dark"
      />
      <div>
        <label className="text-[11px] uppercase tracking-wider text-purple-300/50 block mb-1.5">Data di nascita</label>
        <input
          type="date"
          value={person.birth_date}
          onChange={(e) => setPerson({ ...person, birth_date: e.target.value })}
          data-testid={`${prefix}-birthdate`}
          className="input-dark"
        />
      </div>
      <div>
        <label className="text-[11px] uppercase tracking-wider text-purple-300/50 block mb-1.5">Ora di nascita (facoltativa)</label>
        <input
          type="time"
          value={person.birth_time}
          onChange={(e) => setPerson({ ...person, birth_time: e.target.value })}
          data-testid={`${prefix}-birthtime`}
          className="input-dark"
        />
      </div>
      <div>
        <label className="text-[11px] uppercase tracking-wider text-purple-300/50 block mb-1.5">Luogo di nascita (facoltativo)</label>
        <input
          list="coppia-cities"
          value={person.birth_place}
          onChange={(e) => setPerson({ ...person, birth_place: e.target.value })}
          placeholder="Es. Roma"
          maxLength={80}
          data-testid={`${prefix}-birthplace`}
          className="input-dark"
        />
      </div>
    </div>
  </div>
);

const ChartLine = ({ name, chart, testid }) => (
  <div className="flex items-center justify-between gap-3 flex-wrap">
    <p className="text-purple-100/85 text-sm" data-testid={testid}>
      <span className="text-amber-100 font-medium">{name}</span>
      <span className="text-purple-300/70"> — Sole in {chart.sun} · Luna in {chart.moon}</span>
      {chart.ascendant
        ? <span className="text-purple-300/70"> · Ascendente {chart.ascendant}</span>
        : <span className="text-purple-300/50 text-xs"> · senza ora di nascita: Ascendente e case non calcolabili con precisione</span>}
    </p>
    <CopyReadingButton
      testid={`${testid}-copy`}
      text={`${name} — Sole in ${chart.sun} · Luna in ${chart.moon}${chart.ascendant ? ` · Ascendente ${chart.ascendant}` : ""}`}
    />
  </div>
);

export default function Coppia({ embedded = false }) {
  const { user, loading } = useAuth();
  const [gate, setGate] = useState(false);
  const [couple, setCouple] = useState(undefined);
  const [charts, setCharts] = useState(null);
  const [moon, setMoon] = useState(null);
  const [history, setHistory] = useState([]);
  const [today, setToday] = useState("");
  const [date, setDate] = useState("");
  const [analysis, setAnalysis] = useState(undefined);
  const [generating, setGenerating] = useState(false);
  const [editing, setEditing] = useState(false);
  const [p1, setP1] = useState(empty);
  const [p2, setP2] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [synthesis, setSynthesis] = useState(null);
  const [synthBusy, setSynthBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/entitlements").then((r) => {
      if (r.data.plan !== "sub" && r.data.plan !== "admin") setGate(true);
    }).catch(() => {});
    api.get("/couple").then((r) => {
      setCouple(r.data.couple);
      setCharts(r.data.charts || null);
      setMoon(r.data.moon || null);
      setHistory(r.data.history || []);
      setToday(r.data.today);
      setDate(r.data.today);
      setAnalysis(r.data.analysis || null);
      setSynthesis(r.data.synthesis || null);
      if (r.data.couple) {
        setP1({ name: r.data.couple.p1.name, birth_date: r.data.couple.p1.birth_date, birth_time: r.data.couple.p1.birth_time || "", birth_place: r.data.couple.p1.birth_place || "" });
        setP2({ name: r.data.couple.p2.name, birth_date: r.data.couple.p2.birth_date, birth_time: r.data.couple.p2.birth_time || "", birth_place: r.data.couple.p2.birth_place || "" });
      }
    }).catch(() => setCouple(null));
  }, []);
  useEffect(() => { if (user) load(); }, [user, load]);

  if (loading) return null;

  if (!user) {
    return (
      <div className="max-w-md mx-auto px-4 py-24 text-center" data-testid="coppia-login-required">
        <div className="mystic-card p-10">
          <Heart className="w-10 h-10 text-amber-400/60 mx-auto mb-5" />
          <h1 className="font-display text-3xl text-amber-100 mb-4">La nostra Coppia</h1>
          <p className="text-purple-200/70 text-sm mb-8">
            Tra i vostri cieli, giorno dopo giorno: accedi o crea un account per custodire la vostra coppia.
          </p>
          <Link to="/accedi?redirect=/coppia" data-testid="coppia-login-btn" className="gold-btn inline-block px-8 py-3 text-sm">
            Accedi o registrati
          </Link>
        </div>
      </div>
    );
  }

  if (gate) {
    return (
      <div className={embedded ? "w-full" : "max-w-md mx-auto px-4 py-24"} data-testid="coppia-gate">
        {!embedded && (
          <div className="text-center mb-8">
            <p className="eyebrow">Funzione riservata</p>
            <h1 className="font-display text-4xl text-amber-100 mt-4">La nostra Coppia</h1>
            <p className="text-purple-200/60 text-sm mt-3">Tra i vostri cieli, giorno dopo giorno.</p>
          </div>
        )}
        <div className="mystic-card p-8 text-center">
          <Crown className="w-7 h-7 text-amber-400 mx-auto mb-4" />
          <p className="text-purple-100/85 text-sm leading-relaxed mb-7">
            L'analisi astrologica della coppia — calcolata sui vostri temi natali reali, con sinastria e transiti
            del giorno — è riservata agli abbonati di Arcana. Non consuma consultazioni: con l'abbonamento
            è sempre disponibile, e l'analisi si genera solo quando la richiedi tu.
          </p>
          <Link to="/piani" data-testid="coppia-gate-cta" className="gold-btn px-8 py-3 text-sm inline-block">
            Scopri l'abbonamento
          </Link>
        </div>
      </div>
    );
  }

  const save = async () => {
    if (!p1.name.trim() || !p1.birth_date || !p2.name.trim() || !p2.birth_date) {
      toast.error("Servono nome e data di nascita per entrambe le persone.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        p1: { ...p1, name: p1.name.trim(), birth_time: p1.birth_time || null, birth_place: p1.birth_place || null },
        p2: { ...p2, name: p2.name.trim(), birth_time: p2.birth_time || null, birth_place: p2.birth_place || null },
      };
      const r = await api.put("/couple", payload);
      setCouple(r.data.couple);
      setCharts(r.data.charts);
      setMoon(r.data.moon);
      setHistory(r.data.history || []);
      setToday(r.data.today);
      setDate(r.data.today);
      setAnalysis(r.data.analysis || null);
      setSynthesis(r.data.synthesis || null);
      setEditing(false);
      toast.success("La vostra coppia è custodita.");
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setSaving(false);
    }
  };

  const pickDate = async (d) => {
    setDate(d);
    setAnalysis(undefined);
    try {
      const r = await api.get(`/couple/analysis/${d}`);
      setAnalysis(r.data);
    } catch {
      setAnalysis(null);
    }
  };

  const generate = async () => {
    if (generating) return;
    setGenerating(true);
    try {
      const r = await api.post("/couple/analyze", { date });
      setAnalysis(r.data);
      if (!history.some((h) => h.date === r.data.date)) {
        setHistory([{ date: r.data.date, created_at: r.data.created_at }, ...history]);
      }
      toast.success(r.data.cached ? "Questa analisi era già stata generata: la rivedi senza nuovi calcoli." : "Analisi generata.");
    } catch (e) {
      toast.error(fmtErr(e, "Generazione non riuscita. Riprova."));
    } finally {
      setGenerating(false);
    }
  };

  const genSynthesis = async () => {
    if (synthBusy) return;
    setSynthBusy(true);
    try {
      const r = await api.post("/couple/synthesis");
      setSynthesis(r.data.synthesis);
      toast.success("La sintesi della vostra coppia è pronta.");
    } catch (e) {
      toast.error(fmtErr(e, "Generazione non riuscita. Riprova."));
    } finally {
      setSynthBusy(false);
    }
  };

  const showForm = couple === null || editing;
  const fmtDay = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" });

  return (
    <div className={embedded ? "w-full" : "max-w-3xl mx-auto px-4 lg:px-8 py-16 sm:py-20"} data-testid="coppia-page">
      <datalist id="coppia-cities">
        {CITIES.map((c) => <option key={c} value={c} />)}
      </datalist>

      {!embedded && (
        <div className="text-center mb-10">
          <p className="eyebrow">Il tuo spazio personale</p>
          <h1 className="font-display text-4xl sm:text-5xl text-amber-100 mt-4">La nostra Coppia</h1>
          <p className="text-purple-200/70 mt-3 text-sm">Tra i vostri cieli, giorno dopo giorno.</p>
        </div>
      )}

      {couple === undefined ? null : showForm ? (
        <div className="mystic-card p-7 sm:p-9" data-testid="coppia-form">
          <p className="text-purple-200/70 text-sm mb-6">
            {couple === null
              ? "Crea la vostra coppia: i calcoli astronomici sono reali e l'analisi si genera solo quando la richiedi tu. Ne custodiamo una sola per account, modificabile in qualsiasi momento."
              : "Modifica i dati della coppia: i temi natali si ricalcolano subito."}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <PersonForm label="Prima persona" person={p1} setPerson={setP1} prefix="coppia-p1" />
            <PersonForm label="Seconda persona" person={p2} setPerson={setP2} prefix="coppia-p2" />
          </div>
          <div className="flex items-center justify-center gap-4 mt-7">
            <button onClick={save} disabled={saving} data-testid="coppia-save-btn" className="gold-btn px-8 py-2.5 text-xs disabled:opacity-50">
              {saving ? "Un attimo…" : couple === null ? "Crea la coppia" : "Salva le modifiche"}
            </button>
            {editing && (
              <button onClick={() => setEditing(false)} data-testid="coppia-cancel-btn" className="text-xs text-purple-300/70 hover:text-amber-200 transition-colors">
                Annulla
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="cielo-card p-7 space-y-7" data-testid="coppia-main-card">
          <div data-testid="coppia-header">
            <div className="flex items-center justify-center gap-3 text-amber-100 font-display text-xl mb-4">
              <span data-testid="coppia-p1-label">{couple.p1.name}</span>
              <Heart className="w-4 h-4 text-amber-400 fill-amber-400" />
              <span data-testid="coppia-p2-label">{couple.p2.name}</span>
            </div>
            {charts && (
              <div className="space-y-1.5 text-center">
                <ChartLine name={couple.p1.name} chart={charts.p1} testid="coppia-chart-p1" />
                <ChartLine name={couple.p2.name} chart={charts.p2} testid="coppia-chart-p2" />
              </div>
            )}
            {moon && (
              <p className="text-center text-purple-300/50 text-xs mt-4" data-testid="coppia-moon">
                <MoonStar className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
                {moon.phase} in {moon.moon_sign} oggi
              </p>
            )}
            <div className="text-center">
              <button onClick={() => setEditing(true)} data-testid="coppia-edit-btn" className="mt-4 text-xs text-purple-300/60 hover:text-amber-200 transition-colors inline-flex items-center gap-1.5">
                <PenLine className="w-3.5 h-3.5" /> Modifica la coppia
              </button>
            </div>
          </div>

          <div className="border-t border-[#4a2175]/50 pt-6" data-testid="coppia-synthesis-card">
            <p className="eyebrow mb-4">La vostra dinamica</p>
            {synthesis ? (
              <>
                <div className="text-purple-100/85 text-sm leading-loose whitespace-pre-line" data-testid="coppia-synthesis-text">
                  <MarkdownText text={synthesis} />
                </div>
                <div className="mt-4 flex justify-center gap-2 flex-wrap">
                  <CopyReadingButton text={synthesis} testid="copy-synthesis-coppia" />
                </div>
              </>
            ) : (
              <>
                <p className="text-purple-200/70 text-sm mb-5 leading-relaxed">
                  Una vera lettura della coppia, calcolata sui vostri due cieli insieme: come interagiscono i vostri Soli, le vostre Lune e gli altri pianeti — dove c'è sintonia, come vi incontrate emotivamente e quali contrasti possono emergere.
                </p>
                <button onClick={genSynthesis} disabled={synthBusy} data-testid="coppia-synthesis-btn" className="gold-btn px-7 py-2.5 text-xs disabled:opacity-50 inline-flex items-center gap-2">
                  {synthBusy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  {synthBusy ? "Leggo i vostri cieli insieme…" : "Genera la sintesi della vostra coppia"}
                </button>
              </>
            )}
          </div>

          <div className="border-t border-[#4a2175]/50 pt-6" data-testid="coppia-generate-card">
            <p className="eyebrow mb-4">Analisi della giornata</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <input
                type="date"
                value={date}
                max={today}
                onChange={(e) => pickDate(e.target.value)}
                data-testid="coppia-date-input"
                className="input-dark sm:w-auto"
              />
              <button
                onClick={generate}
                disabled={generating || !date || analysis === undefined}
                data-testid="coppia-generate-btn"
                className="gold-btn px-7 py-2.5 text-xs disabled:opacity-50 inline-flex items-center gap-2"
              >
                {generating && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                {generating ? "Calcolo i vostri cieli…" : analysis ? "Rigenera l'analisi" : "Genera l'analisi"}
              </button>
            </div>
            <p className="text-[11px] text-purple-300/50 text-center mt-3">
              L'analisi si genera solo quando la richiedi: per la stessa data rivedrai quella già creata, senza nuovi calcoli.
            </p>
          </div>
          </div>

          {analysis === undefined ? (
            <p className="text-center text-purple-300/50 text-sm py-4" data-testid="coppia-analysis-loading">Un attimo…</p>
          ) : analysis && (
            <div className="cielo-card p-7 sm:p-8" data-testid="coppia-analysis">
              <p className="eyebrow mb-4">La vostra giornata · {fmtDay(analysis.date)}</p>
              {analysis.moon && (
                <p className="text-[11px] uppercase tracking-wider text-purple-300/50 mb-4">
                  {analysis.moon.phase} · Luna in {analysis.moon.moon_sign}
                </p>
              )}
              <div className="text-purple-100/85 text-sm leading-loose whitespace-pre-line" data-testid="coppia-analysis-text">
                <MarkdownText text={analysis.analysis} />
                <div className="mt-4 flex justify-center gap-2 flex-wrap">
                  <CopyReadingButton text={analysis.analysis} testid="copy-analysis-coppia" />
                </div>
              </div>
              {analysis.cached && (
                <p className="text-[10px] text-purple-300/40 mt-5">Analisi già generata per questa data: la stai rileggendo senza nuovi calcoli.</p>
              )}
            </div>
          )}

          {history.length > 0 && (
            <div className="cielo-card p-6" data-testid="coppia-history">
              <p className="eyebrow mb-3 flex items-center gap-2"><History className="w-3.5 h-3.5" /> Analisi precedenti</p>
              <div className="flex flex-wrap gap-2">
                {history.map((h) => (
                  <button
                    key={h.date}
                    onClick={() => pickDate(h.date)}
                    data-testid={`coppia-history-${h.date}`}
                    className={`text-xs rounded-full px-3.5 py-1.5 border transition-colors ${h.date === date ? "border-amber-500/60 text-amber-200 bg-amber-500/10" : "border-[#4a2175] text-purple-300/70 hover:border-amber-500/40 hover:text-amber-200"}`}
                  >
                    {fmtDay(h.date)}
                  </button>
                ))}
              </div>
            </div>
          )}

          <p className="text-center text-purple-300/40 text-xs flex items-center justify-center gap-1.5" data-testid="coppia-disclaimer">
            <Sparkles className="w-3.5 h-3.5" /> Una lettura astrologica di riflessione, non un consulto di cartomanzia.
          </p>
        </div>
      )}
    </div>
  );
}
